import React, { useEffect, useState } from "react";
import Header from "../../../components/common/Header/Header";
import useMQTT from "../../../hooks/useMQTT";
import Command from "../../../classes/command.class";
import { useParams } from "react-router-dom";
import { fetchSpa } from "../../../redux/actions/spa.actions";
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import "./SpaBoy.css";
import { LevelScale } from "./components/LevelScale";
import Stepper from "../../../components/common/Stepper/Stepper";
import { HeartIcon, PersonIcon } from "../../../components/common/Icons/Icons";
import { CHLORINE_RANGES, PH_RANGES, USAGE_LEVELS, CELL_LIFE_SEGMENTS, cellLifeSegments } from "../../../utils/waterCare";
import { SpaConstLabels } from "../../../mqttData/consts/spaConstsLabels.enums";
import { inputDebounce, settingsDebounce } from "../debounceTimes";
import { SpaLiveLabels } from "../../../mqttData/live/spaLiveLabels.enums";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { commandKeys } from "../../../mqttData/commands/commands.enum";
import { SpaSettingsLabels } from "../../../mqttData/settings/spaSettingsLabels";
import { SmartPHState, SmartPHStateTextMap } from "../../../mqttData/live/smartPhState.enums";
import { palName } from "../../../utils/brand";

const SpaBoy = () => {
  const { id } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const liveData = useSelector((state: RootState) => state.live.data);
  const settingsData = useSelector((state: RootState) => state.settings.data);
  const constData = useSelector((state: RootState) => state.const.data);
  const isConnected = useSelector((state: RootState) => 
    state.spa.currentSpaId === id && state.spa.isCurrentSpaConnected
  );
  const isConnecting = useSelector((state: RootState) => 
    state.spa.currentSpaId === id && state.spa.isConnecting
  );

  const {canAccess:canEnableManualMode} = usePermissionCheck(['can_enable_manual_mode'])

  const [orpLevelBase, setOrpLevelBase] = useState(0);
  const [spaboyHoursPerDay, setSpaboyHoursPerDay] = useState(0);

  // Debounce logic states
  const [lastUserORP, setLastUserORP] = useState<number | null>(null);
  const [lastUpdateORP, setLastUpdateORP] = useState<number>(0);
  const [lastUserSBHrs, setLastUserSBHrs] = useState<number | null>(null);
  const [lastUpdateSBHrs, setLastUpdateSBHrs] = useState<number>(0);

  let orpLevelMapper: { [p: number]: [number, number] };

  orpLevelMapper = {
    0: [545, 555],
    50: [645, 655],
    100: [745, 755],
  };


  // Fetch SPA From the DB
  useEffect(() => {
    if (id != null) {
      dispatch(fetchSpa(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (settingsData.SBORPlo !== undefined) {
      const now = Date.now();
      const backendORP = ((settingsData.SBORPlo as number) - 545) / 2;
      if (
        lastUserORP === null &&
        now - lastUpdateORP > settingsDebounce
      ) {
        setOrpLevelBase(backendORP);
      }
      if (lastUserORP !== null && backendORP === lastUserORP) {
        setLastUserORP(null);
      }
    }
  }, [settingsData.SBORPlo]);

  useEffect(() => {
    if (lastUserORP !== null && orpLevelBase === lastUserORP) {
      const timer = setTimeout(() => {
        command.updateSettings({
          SBORPlo: orpLevelMapper[lastUserORP][0],
          SBORPhi: orpLevelMapper[lastUserORP][1],
        });
      }, inputDebounce);
      return () => clearTimeout(timer);
    }
  }, [lastUserORP, orpLevelBase]);

  useEffect(() => {
    if (settingsData.SBHr !== undefined) {
      const now = Date.now();
      const backendSBHrs = settingsData.SBHr as number;
      if (
        lastUserSBHrs === null &&
        now - lastUpdateSBHrs > settingsDebounce
      ) {
        setSpaboyHoursPerDay(backendSBHrs);
      }
      if (lastUserSBHrs !== null && backendSBHrs === lastUserSBHrs) {
        setLastUserSBHrs(null);
      }
    }
  }, [settingsData.SBHr]);

  useEffect(() => {
    if (lastUserSBHrs !== null && spaboyHoursPerDay === lastUserSBHrs) {
      const timer = setTimeout(() => {
        command.updateSettings({ setSBHrs: lastUserSBHrs });
      }, inputDebounce);
      return () => clearTimeout(timer);
    }
  }, [lastUserSBHrs, spaboyHoursPerDay]);

  // Handlers for user input (match SpaControlPage)
  const handleClLevelChange = (value:number) => {
    setOrpLevelBase(value);
    setLastUserORP(value);
    setLastUpdateORP(Date.now());
  };
  const handleSpaboyHoursChange = (value: number) => {
    setSpaboyHoursPerDay(value);
    setLastUserSBHrs(value);
    setLastUpdateSBHrs(Date.now());
  };

  // MQTT Functionality:
  const [commandTopic] = useMQTT(id ?? "");
  const command = new Command(commandTopic);

  const isOffline = !isConnected && !isConnecting;
  const controlsDisabled = !isConnected;
  // Manual mode (fixed production hours) is only offered with the permission.
  const isManual = canEnableManualMode && spaboyHoursPerDay > 0;
  const maxHours = constData[SpaConstLabels.setSBHrsmax] ?? 24;

  const cellLife = cellLifeSegments(liveData[SpaLiveLabels.sbWear]);
  const wear = liveData[SpaLiveLabels.sbWear];
  const boosting = Boolean(liveData[SpaLiveLabels.sbBoost]);
  const producing = Boolean(liveData[SpaLiveLabels.sbProducing]);
  const phState = (liveData[SpaLiveLabels.phSM] ?? 0) as SmartPHState;
  const hasSmartPH = Boolean(settingsData[SpaSettingsLabels.cfgSP]);

  return (
    <div className="page spaboy-page">
      <Header logo="spaboy"/>
      {isConnecting ?
      <div className="spaboy-loading"><span className="spinner" role="progressbar" aria-label="Loading"/></div>
      :
      <main className={`spaboy-main ${isOffline ? 'offline' : ''}`}>
        {isOffline && <div className="page-notice">Spa is offline</div>}

        {/* What the cell / smart pH are doing right now */}
        <div className="spaboy-status">
          <span className={`chip sb-chip ${producing && isConnected ? 'on' : ''}`}>
            {palName} {producing && isConnected ? 'Producing' : 'Idle'}
          </span>
          {hasSmartPH && isConnected && phState !== SmartPHState.IDLE &&
            <span className="chip sb-chip on">Smart pH: {SmartPHStateTextMap[phState]}</span>
          }
        </div>

        {/* Chlorine + pH as five-zone scales with a marker on the current level.
            Hover a row for the raw sensor value. */}
        <div className="levels">
          <LevelScale
            label="CL"
            value={isConnected ? liveData[SpaLiveLabels.sbORPind] : undefined}
            ranges={CHLORINE_RANGES}
            unit="ppm (FCL)"
            detail={`${liveData[SpaLiveLabels.sbORP] ?? 0} mV`}
          />
          <LevelScale
            label="pH"
            value={isConnected ? liveData[SpaLiveLabels.sbpHind] : undefined}
            ranges={PH_RANGES}
            detail={`pH ${(liveData[SpaLiveLabels.sbpH] ?? 0) / 100}`}
          />
        </div>

        {/* Spa usage (auto: sets the ORP window), or fixed hours in manual mode */}
        <section className="spaboy-section">
          <div className="spaboy-section-head">
            <span className="spaboy-section-title">{isManual ? 'Production Hours' : 'Spa Usage'}</span>
            {canEnableManualMode &&
              <div className="segmented" role="group" aria-label={`${palName} mode`}>
                <button
                  className={!isManual ? 'active' : ''}
                  aria-pressed={!isManual}
                  disabled={controlsDisabled}
                  onClick={() => isManual && handleSpaboyHoursChange(0)}
                >
                  Auto
                </button>
                <button
                  className={isManual ? 'active' : ''}
                  aria-pressed={isManual}
                  disabled={controlsDisabled}
                  onClick={() => !isManual && handleSpaboyHoursChange(1)}
                >
                  Manual
                </button>
              </div>
            }
          </div>

          {isManual ?
            <div className="spaboy-hours">
              <Stepper
                label="Production hours"
                hideLabel
                value={spaboyHoursPerDay}
                unit={spaboyHoursPerDay === 1 ? 'hour / day' : 'hours / day'}
                onDecrement={() => handleSpaboyHoursChange(spaboyHoursPerDay - 1)}
                onIncrement={() => handleSpaboyHoursChange(spaboyHoursPerDay + 1)}
                decrementDisabled={spaboyHoursPerDay <= 1 || controlsDisabled}
                incrementDisabled={spaboyHoursPerDay >= maxHours || controlsDisabled}
              />
            </div>
            :
            <div className="usage-row">
              {USAGE_LEVELS.map((level) => (
                <button
                  key={level.label}
                  className={`usage-pill ${orpLevelBase === level.base ? 'active' : ''}`}
                  aria-pressed={orpLevelBase === level.base}
                  disabled={controlsDisabled}
                  onClick={() => handleClLevelChange(level.base)}
                >
                  <span className="usage-people">
                    {Array.from({ length: level.people }).map((_, i) => <PersonIcon key={i} />)}
                    {level.plus && <span className="usage-plus">+</span>}
                  </span>
                  <span className="usage-label">{level.label}</span>
                </button>
              ))}
            </div>
          }
        </section>

        {/* Cell life: remaining electrode life as five bars */}
        <section className="spaboy-section">
          <div className="spaboy-section-head">
            <span className="spaboy-section-title cell-life-title"><HeartIcon />Cell Life</span>
            <span className="cell-life-status">
              {cellLife.tone === 'measuring' ? 'Measuring' : `${wear ?? 100}%`}
            </span>
          </div>
          <div
            className={`cell-life ${cellLife.tone}`}
            role="meter"
            aria-label="Cell life"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={cellLife.tone === 'measuring' ? undefined : Number(wear ?? 100)}
            aria-valuetext={cellLife.tone === 'measuring' ? 'Measuring' : undefined}
          >
            {Array.from({ length: CELL_LIFE_SEGMENTS }).map((_, i) => (
              <span key={i} className={`cell-life-bar ${i < cellLife.filled ? 'filled' : ''}`} />
            ))}
          </div>
        </section>

        {/* Boosts */}
        <section className="spaboy-section">
          <div className="boost-row">
            <button
              className={`boost-button ${boosting ? 'active' : ''}`}
              disabled={controlsDisabled}
              onClick={() => command.executeSingle(commandKeys.SBBoost)}
            >
              {boosting ? 'Boosting…' : 'Sanitizer Boost'}
            </button>

          </div>
        </section>
      </main>
      }
    </div>
  );
};

export default SpaBoy;
